import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ScrollToTop from '../components/ScrollToTop';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    serviceInterest: '',
    message: '',
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://contact.vinayakrobotics.com/api/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send message');
      }

      toast({
        title: 'Message Sent Successfully!',
        description: "We've received your inquiry and sent a confirmation email.",
        duration: 6000,
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        serviceInterest: '',
        message: '',
      });
    } catch (err) {
      console.error(err);
      toast({
        title: 'Error Sending Message',
        description: err.message || 'Something went wrong. Please try again.',
        duration: 6000,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-primary">
      <Header />
      <ScrollToTop />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 relative overflow-hidden">
        <div className="absolute top-20 right-10 w-96 h-96 rounded-full blur-3xl opacity-10" style={{ background: '#F97316' }} />
        <div className="absolute bottom-20 left-10 w-96 h-96 rounded-full blur-3xl opacity-10" style={{ background: '#A64425' }} />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-foreground-primary mb-6">Get In Touch</h1>
          <p className="text-xl text-foreground-secondary">
            Ready to transform your industrial inspection processes? Let's discuss how our solutions can help.
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="pb-24 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12">
          <div className="glass-frosted rounded-3xl p-10 glow-subtle">
            <h2 className="text-3xl font-bold text-foreground-primary mb-8">Send Us a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <InputField id="name" label="Full Name *" value={formData.name} onChange={(v) => handleChange('name', v)} placeholder="Rajesh Kumar" />
                <InputField id="email" label="Email Address *" value={formData.email} onChange={(v) => handleChange('email', v)} placeholder="rajesh@company.in" type="email" />
                  <InputField id="phone" label="Phone Number" value={formData.phone} onChange={(v) => handleChange('phone', v)} placeholder="+91 98765 43210" />
                    <InputField id="company" label="Company Name" value={formData.company} onChange={(v) => handleChange('company', v)} placeholder="Your Company" />

                      <div>
                        <Label htmlFor="serviceInterest" className="text-foreground-primary mb-2 block">Service Interest</Label>
                        <Select value={formData.serviceInterest} onValueChange={(v) => handleChange('serviceInterest', v)}>
                          <SelectTrigger className="glass-effect border-subtle text-foreground-primary">
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pipeline">Pipeline Inspection (Vulcan)</SelectItem>
                            <SelectItem value="hdd">HDD Support (Vulcan)</SelectItem>
                            <SelectItem value="tunnel">Tunnel Surveying (Vulcan)</SelectItem>
                            <SelectItem value="maintenance">Industrial Maintenance (Vulcan)</SelectItem>
                            <SelectItem value="field-measurement">Field Measurement (Astra)</SelectItem>
                            <SelectItem value="custom">Custom Solution</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="message" className="text-foreground-primary mb-2 block">Message *</Label>
                        <Textarea
                          id="message"
                          required
                          value={formData.message}
                          onChange={(e) => handleChange('message', e.target.value)}
                          className="glass-effect border-subtle text-foreground-primary placeholder:text-foreground-muted min-h-[150px]"
                          placeholder="Tell us about your project requirements..."
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full py-6 text-lg font-semibold transition-smooth"
                        style={{
                          background: isSubmitting ? '#666' : '#F97316',
                          color: '#FFFFFF',
                        }}
                      >
                        {isSubmitting ? 'Sending...' : <span className="flex items-center justify-center gap-2"><Send size={20} /> Send Message</span>}
                        </Button>
                      </form>
                    </div>

          {/* Contact Info */}
                    <ContactInfo />
                  </div>
                </section>

                <Footer />
              </div>
              );
};

export default Contact;

// ------------------ Components ------------------

const InputField = ({ id, label, value, onChange, placeholder, type = 'text' }) => (
  <div>
    <Label htmlFor={id} className="text-foreground-primary mb-2 block">{label}</Label>
    <Input
      id={id}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="glass-effect border-subtle text-foreground-primary placeholder:text-foreground-muted"
    />
  </div>
  );

const ContactInfo = () => (
  <div className="space-y-8">
    <div className="glass-frosted rounded-3xl p-10 glow-subtle">
      <h2 className="text-3xl font-bold text-foreground-primary mb-8">Contact Information</h2>
      <div className="space-y-6">
        <InfoItem Icon={Mail} title="Email" lines={['contact@vinayakrobotics.com']} />
        <InfoItem Icon={Phone} title="Phone" lines={['+91 33 4812 7214','Mon-Sat, 10AM-5PM IST']} />
        <InfoItem Icon={MapPin} title="Address" lines={['DN-10 Merlin Matrix, Suite #103','Saltlake Sector-5, Kolkata 700 091']} />
      </div>
    </div>
    <div className="glass-frosted rounded-3xl p-10 glow-subtle">
      <h3 className="text-2xl font-bold text-foreground-primary mb-4">Why Choose Us?</h3>
      <div className="space-y-4">
        {[
          'Industry-leading autonomous robotics',
          '24/7 technical support',
          'Custom solutions for unique challenges',
          'Proven track record in extreme environments',
        ].map((item, index) => (
          <div key={index} className="flex items-start gap-3">
            <CheckCircle size={20} style={{ color: '#F97316', marginTop: '2px' }} />
            <span className="text-foreground-secondary">{item}</span>
          </div>
          ))}
      </div>
    </div>
  </div>
  );

const InfoItem = ({ Icon, title, lines }) => (
  <div className="flex items-start gap-4">
    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(249, 115, 22, 0.1)', border: '1px solid rgba(249, 115, 22, 0.3)' }}>
      <Icon size={24} style={{ color: '#F97316' }} />
    </div>
    <div>
      <h3 className="text-lg font-semibold text-foreground-primary mb-1">{title}</h3>
      {lines.map((line, i) => <p key={i} className="text-foreground-secondary">{line}</p>)}
      </div>
    </div>
    );